import { useMemo } from 'react';
import { defineColumn } from '../../../components/tanstack-table/column';
import { TanStackTable } from '../../../components/tanstack-table/table';
import type { ProductOrderMatch } from '../../../data/receiving';
import {
  emDashIfEmpty,
  productHeadColumns,
  productTailColumns,
  vendorColumn,
} from './product-columns';

// Backend discriminant for matches produced by the suggested-item-code
// heuristic. Rows with this value go to the Speculative table and get the
// amber highlight on their Item code cell.
const SPECULATIVE_MATCH_METHOD = 'suggested_item_code';

const matchProduct = (m: ProductOrderMatch) => m.product;

const itemCodeColumn = defineColumn<ProductOrderMatch>({
  id: 'Item code',
  minWidth: 100,
  maxWidth: 160,
  accessorFn: (m) =>
    emDashIfEmpty(m.product.item_code || m.product.suggested_item_code || m.product.raw_item_code),
  cell: ({ item: m, value }) => (
    <span
      className={`font-mono ${m.matched_by === SPECULATIVE_MATCH_METHOD ? 'bg-amber-100 px-1' : ''}`}
    >
      {value}
    </span>
  ),
});

const matchedByColumn = defineColumn<ProductOrderMatch>({
  id: 'Matched by',
  minWidth: 140,
  maxWidth: 200,
  accessorFn: (m) => m.matched_by,
  cell: ({ value }) => <span className="text-xs text-gray-500">{value}</span>,
});

export function SpeculativeMatchesTable({
  rows,
  vendorNames,
}: {
  rows: ProductOrderMatch[];
  vendorNames?: Map<string, string>;
}) {
  const columns = useMemo(() => {
    const head = productHeadColumns<ProductOrderMatch>(matchProduct);
    const tail = productTailColumns<ProductOrderMatch>(matchProduct);
    const showVendor = (vendorNames?.size ?? 0) > 1;

    return showVendor
      ? [
          ...head,
          vendorColumn<ProductOrderMatch>(matchProduct, (id) => vendorNames?.get(id) ?? ''),
          itemCodeColumn,
          matchedByColumn,
          ...tail,
        ]
      : [...head, itemCodeColumn, matchedByColumn, ...tail];
  }, [vendorNames]);

  return <TanStackTable data={rows} columns={columns} />;
}
